import { useEffect, useState } from "react";
import client, { apiError } from "@/api/client";
import { PageHeader, Section } from "@/components/PageChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useUserNames } from "@/hooks/useUserNames";
import { toast } from "sonner";
import { ROLES, roleLabel, userRoles } from "@/lib/constants";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const EMPTY = { mobile: "", name: "", password: "", roles: ["salesman"] };

const toggleRole = (current, r) => {
  if (r === "admin") return current.includes("admin") ? [] : ["admin"];
  const rest = current.filter((x) => x !== "admin");
  return rest.includes(r) ? rest.filter((x) => x !== r) : [...rest, r];
};

const RolePicker = ({ value, onChange }) => (
  <div className="flex flex-wrap gap-2" data-testid="user-roles-picker">
    {ROLES.map((r) => {
      const on = value.includes(r.value);
      return (
        <button type="button" key={r.value} data-testid={`user-role-chip-${r.value}`} aria-pressed={on}
          onClick={() => onChange(toggleRole(value, r.value))}
          className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm border transition-colors ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
          {r.label}
        </button>
      );
    })}
  </div>
);

export default function Users() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const { nameFor } = useUserNames();

  const load = async () => { const { data } = await client.get("/users"); setRows(data); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (u) => { setForm({ mobile: u.mobile, name: u.name, password: "", roles: userRoles(u).filter((r) => r !== "superuser") }); setEditId(u.id); setOpen(true); };
  const save = async () => {
    if (!form.roles.length) { toast.error("Select at least one role"); return; }
    try {
      if (editId) {
        const payload = { name: form.name, roles: form.roles };
        if (form.password) payload.password = form.password;
        await client.put(`/users/${editId}`, payload);
      } else {
        await client.post("/users", form);
      }
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this user?")) return;
    try { await client.delete(`/users/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Users" subtitle="Access & roles"
        actions={<Button data-testid="user-new-btn" onClick={openNew} className="bg-slate-900 hover:bg-slate-800 rounded-sm"><Plus size={16} weight="bold" className="mr-1"/> New User</Button>}
      />
      <Section>
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left label-cap border-b border-slate-200 bg-slate-50">
              <th className="px-4 py-3">Mobile</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Roles</th>
              <th className="px-4 py-3">Entered By</th><th className="px-4 py-3 w-24">Actions</th>
            </tr></thead>
            <tbody data-testid="users-tbody">
              {rows.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-2 font-mono">{u.mobile}</td>
                  <td className="px-4 py-2 font-semibold">{u.name}</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1" data-testid={`user-roles-${u.mobile}`}>
                      {userRoles(u).map((r) => (
                        <span key={r} className="inline-flex px-2 py-0.5 text-xs font-semibold uppercase tracking-wider rounded-sm bg-slate-100 text-slate-700 border border-slate-200">{roleLabel(r)}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{nameFor(u.created_by)}</td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      <button data-testid={`user-edit-${u.mobile}`} onClick={() => openEdit(u)} className="p-1.5 hover:bg-slate-100 rounded-sm"><PencilSimple size={14}/></button>
                      <button data-testid={`user-delete-${u.mobile}`} onClick={() => remove(u.id)} className="p-1.5 hover:bg-rose-50 text-rose-600 rounded-sm"><Trash size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">No users</td></tr>}
            </tbody>
          </table>
        </div>
      </Section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "New"} User</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div>
              <label className="label-cap">Mobile</label>
              <Input data-testid="user-mobile-input" value={form.mobile} onChange={(e)=>setForm({...form, mobile: e.target.value})} disabled={!!editId}/>
            </div>
            <div><label className="label-cap">Name</label><Input data-testid="user-name-input" value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})}/></div>
            <div>
              <label className="label-cap">Password {editId && <span className="normal-case tracking-normal text-slate-400 text-[10px]">(leave blank to keep)</span>}</label>
              <Input data-testid="user-password-input" type="password" value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})}/>
            </div>
            <div>
              <label className="label-cap block mb-1.5">Roles <span className="normal-case tracking-normal text-slate-400 text-[10px]">(Admin can't be combined)</span></label>
              <RolePicker value={form.roles} onChange={(roles)=>setForm({...form, roles})}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="user-save-btn" onClick={save} className="bg-slate-900 hover:bg-slate-800 rounded-sm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
